import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injectable, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../../../book-create/login-validation.component/login-validation.component';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { SalesService } from '../../../shared/services/apis/platform/sales.service';
import { CsvExportService } from '../../../shared/services/csv-export.service';
import { SalesListRequest } from '../../../shared/services/requests/platform/sales-list.request';
import {
  AdminSaleListItem,
  AdminSalesIndicatorsResponse,
  AdminSalesStage,
} from '../../../shared/services/responses/platform/admin-sales.response';
import { UtcDatePipe } from './utc-date.pipe';
import { PAYMENT_METHODS, SALES_CHANNELS, SALES_STATUS, SALES_STATUS_FLOW, stageLabel, statusClass } from './sales-status';
import { SaleDetailsDialogComponent } from './components/sale-details-dialog.component';
import { SalesChartDialogComponent } from './components/sales-chart-dialog.component';
import { NfeUploadDialogComponent } from './components/nfe-upload-dialog.component';

/** Rótulos do paginador em pt-BR. */
@Injectable()
export class SalesPaginatorIntl extends MatPaginatorIntl {
  override itemsPerPageLabel = 'Por página';
  override nextPageLabel = 'Próxima';
  override previousPageLabel = 'Anterior';
  override firstPageLabel = 'Primeira';
  override lastPageLabel = 'Última';
  override getRangeLabel = (page: number, pageSize: number, length: number) => {
    if (length === 0 || pageSize === 0) return '0 de 0';
    const start = page * pageSize + 1;
    const end = Math.min((page + 1) * pageSize, length);
    return `${start}–${end} de ${length}`;
  };
}

@Component({
  selector: 'app-sales-list',
  standalone: true,
  templateUrl: './sales-list.component.html',
  styleUrl: './sales-list.component.css',
  imports: [
    CommonModule, ReactiveFormsModule, MenuComponent, FooterComponent, LoginValidationComponent,
    MatIconModule, MatTooltipModule, MatProgressSpinnerModule, MatButtonModule, MatSelectModule, MatFormFieldModule,
    MatInputModule, MatDatepickerModule, MatNativeDateModule, MatSlideToggleModule, MatMenuModule, MatDividerModule, MatPaginatorModule,
    UtcDatePipe,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: SalesPaginatorIntl }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesListComponent implements OnInit {
  isLoading = false;
  isExporting = false;
  form!: FormGroup;

  readonly statusOptions = ['Todos', ...SALES_STATUS_FLOW];
  readonly paymentMethods = PAYMENT_METHODS;
  readonly channelOptions = SALES_CHANNELS;
  readonly discountOptions = [
    { value: '', label: 'Todos' }, { value: 'none', label: 'Sem desconto' }, { value: 'author', label: 'Desconto de autor' }, { value: 'coupon', label: 'Cupom' },
  ];
  readonly pageSizeOptions = [25, 50, 100];

  list: AdminSaleListItem[] = [];
  total = 0;
  page = 1;
  pageSize = 25;

  indicators: AdminSalesIndicatorsResponse | null = null;
  stages: AdminSalesStage[] = [];

  stageLabel = stageLabel;
  statusClass = statusClass;

  constructor(
    private fb: FormBuilder,
    private salesService: SalesService,
    private csv: CsvExportService,
    private alert: AlertService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.form = this.fb.group({
      search: [''],
      status: ['Todos'],
      idPaymentMethod: [null],
      channel: [''],
      discount: [''],
      dateStart: [null],
      dateEnd: [null],
      onlyPending: [false],
    });
  }

  onLoginValidated(isLogged: boolean) {
    if (!isLogged) return;
    this.fetchIndicators();
    this.fetch();
  }

  // ---------- Indicadores ----------
  fetchIndicators(): void {
    this.salesService.indicators().subscribe({
      next: (res) => {
        if (!res.isError) {
          this.indicators = res;
          this.stages = SALES_STATUS_FLOW.map(st => res.byStage.find(s => s.status === st) ?? { statusCode: 0, status: st, count: 0, lateCount: 0, oldestDays: 0, last30Count: 0 });
        }
        this.cdr.markForCheck();
      },
      error: () => this.cdr.markForCheck(),
    });
  }

  isFinalStage(status: string): boolean {
    return status === SALES_STATUS.entregue || status === SALES_STATUS.finalizado;
  }

  stageClass(status: string): string {
    return statusClass(status).replace('chip ', 'dot-');
  }

  stageSub(s: AdminSalesStage): string {
    if (this.isFinalStage(s.status)) return `${s.last30Count} nos últimos 30 dias`;
    if (s.lateCount) return `${s.lateCount} atrasada${s.lateCount === 1 ? '' : 's'}`;
    if (s.count) return `mais antiga: ${s.oldestDays} d`;
    return 'nenhuma pendente';
  }

  toggleStage(status: string): void {
    const current = this.form.value.status;
    this.form.patchValue({ status: current === status ? 'Todos' : status });
    this.applyFilters();
  }

  openChart(): void {
    this.dialog.open(SalesChartDialogComponent, { maxWidth: '96vw', autoFocus: false });
  }

  // ---------- Lista ----------
  fetch(): void {
    this.isLoading = true;
    this.cdr.markForCheck();
    this.salesService.postList(this.buildRequest()).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.isError) {
          this.alert.show('Erro', res.errorDescription || 'Falha ao carregar vendas.', AlertType.error);
          this.cdr.markForCheck();
          return;
        }
        this.list = res.list ?? [];
        this.total = res.total ?? 0;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isLoading = false;
        this.alert.show('Erro', err?.message || 'Erro inesperado ao carregar a lista.', AlertType.error);
        this.cdr.markForCheck();
      },
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.fetch();
  }

  resetFilters(): void {
    this.form.reset({ search: '', status: 'Todos', idPaymentMethod: null, channel: '', discount: '', dateStart: null, dateEnd: null, onlyPending: false });
    this.applyFilters();
  }

  onPage(ev: PageEvent): void {
    this.page = ev.pageIndex + 1;
    this.pageSize = ev.pageSize;
    this.fetch();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private buildRequest(exportMode?: 'sales' | 'items'): SalesListRequest {
    const v = this.form.value;
    const status = v.status && v.status !== 'Todos' ? [v.status] : null;
    return {
      page: this.page,
      pageSize: this.pageSize,
      search: (v.search || '').trim() || null,
      listStatus: status,
      idPaymentMethod: v.idPaymentMethod || null,
      channel: v.channel || null,
      discount: v.discount || null,
      dateTimeStart: this.startOfDayIso(v.dateStart),
      dateTimeEnd: this.endOfDayIso(v.dateEnd),
      onlyPending: !!v.onlyPending,
      exportMode: exportMode ?? null,
    };
  }

  private startOfDayIso(date: Date | null): string | null {
    if (!date) return null;
    const d = new Date(date); d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }

  private endOfDayIso(date: Date | null): string | null {
    if (!date) return null;
    const d = new Date(date); d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }

  // ---------- Ações ----------
  openDetails(s: AdminSaleListItem): void {
    this.dialog.open(SaleDetailsDialogComponent, {
      width: '860px', maxWidth: '96vw', maxHeight: '94vh', autoFocus: false,
      data: { idKitPayment: s.idKitPayment },
    }).afterClosed().subscribe(changed => { if (changed) this.refreshAll(); });
  }

  openNfe(s: AdminSaleListItem): void {
    this.dialog.open(NfeUploadDialogComponent, {
      width: '720px', maxWidth: '95vw',
      data: { idKitPayment: s.idKitPayment, hasXml: !!s.linkXml, hasDanfe: !!s.linkDanfe },
    }).afterClosed().subscribe(res => { if (res) this.refreshAll(); });
  }

  openUrl(url?: string | null): void {
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  }

  private refreshAll(): void {
    this.fetch();
    this.fetchIndicators();
  }

  // ---------- Exportação ----------
  exportCsv(mode: 'sales' | 'items'): void {
    if (this.isExporting) return;
    this.isExporting = true;
    this.cdr.markForCheck();
    this.salesService.export(this.buildRequest(mode)).subscribe({
      next: (res) => {
        this.isExporting = false;
        if (res.isError) {
          this.alert.show('Erro', res.errorDescription || 'Falha ao exportar.', AlertType.error);
        } else if (mode === 'items') {
          this.saveItemsCsv(res.items ?? []);
        } else {
          this.saveSalesCsv(res.sales ?? []);
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isExporting = false;
        this.alert.show('Erro inesperado', err?.message || 'Falha ao exportar.', AlertType.error);
        this.cdr.markForCheck();
      },
    });
  }

  private discountLabel(type: string): string {
    return type === 'author' ? 'Autor' : type === 'coupon' ? 'Cupom' : '';
  }

  private saveSalesCsv(rows: import('../../../shared/services/responses/platform/admin-sales.response').AdminSalesExportRow[]): void {
    const header = [
      'ID venda', 'Data/hora compra', 'Status', 'Canal', 'Pedido marketplace',
      'Cliente', 'CPF', 'E-mail', 'Telefone',
      'CEP', 'Rua', 'Número', 'Complemento', 'Bairro', 'Cidade', 'UF',
      'Nº livros', 'Itens', 'Tipo desconto', 'Código desconto', 'Valor desconto',
      'Valor produtos', 'Frete', 'Total', 'Forma pagamento', 'Parcelas', 'ID pagamento Asaas',
      'NF-e nº', 'NF-e chave', 'NF-e gerada em', 'XML anexado em', 'DANFE anexado em',
      'Serviço Correios', 'Código serviço', 'Rastreio', 'Pré-postagem', 'Origem etiqueta', 'Etiqueta gerada em',
      'Início da produção', 'Saída da gráfica', 'Entregue em',
    ];
    const data = rows.map(r => [
      r.idKitPayment, this.csv.date(r.saleDateTime, true), r.status, r.channel, r.marketplaceOrderId,
      r.clientName, r.clientCpf, r.clientEmail, r.clientPhone,
      r.deliveryZipcode, r.deliveryAddress, r.deliveryNumber, r.deliveryComplement, r.deliveryProvince, r.deliveryCity, r.deliveryState,
      r.booksCount, r.items, this.discountLabel(r.discountType), r.discountCode, this.csv.money(r.discountValue),
      this.csv.money(r.productsValue), this.csv.money(r.deliveryPrice), this.csv.money(r.finalValue), r.paymentMethodName, r.installmentCount, r.asaasPaymentToken,
      r.nfeNumber, r.nfeKey, this.csv.date(r.datetimeNfeGenerated, true), this.csv.date(r.datetimeXmlInclusion, true), this.csv.date(r.datetimeDanfeInclusion, true),
      r.deliveryTypeName, r.deliveryType, r.coreiosTrackingCode, r.idPostagemCorreios, r.labelSource, this.csv.date(r.datetimeLabelGenerated, true),
      this.csv.date(r.datetimeProductionStarted, true), this.csv.date(r.sendDateTime, true), this.csv.date(r.datetimeDelivered, true),
    ]);
    this.csv.download(this.csv.fileName('vendas'), header, data);
  }

  private saveItemsCsv(rows: import('../../../shared/services/responses/platform/admin-sales.response').AdminSalesExportItemRow[]): void {
    const header = [
      'ID venda', 'Data/hora compra', 'Status', 'Canal', 'Pedido marketplace', 'Cliente',
      'ID item', 'ID kit', 'ID versão', 'Livro', 'Autor', 'Kit', 'Físico', 'E-book', 'Vídeo',
      'Qtd', 'Preço tabela', 'Tipo desconto', 'Código desconto', 'Desconto unit.', 'Preço unit.', 'Subtotal',
    ];
    const data = rows.map(r => [
      r.idKitPayment, this.csv.date(r.saleDateTime, true), r.status, r.channel, r.marketplaceOrderId, r.clientName,
      r.idKitPaymentItem, r.idKit, r.idBookVersion, r.bookTitle, r.authorName, r.kitName, this.csv.bool(r.includesBook), this.csv.bool(r.includesEbook), this.csv.bool(r.includesVideo),
      r.quantity, this.csv.money(r.listPriceUnit), this.discountLabel(r.discountType), r.discountCode, this.csv.money(r.discountValueUnit), this.csv.money(r.priceUnit), this.csv.money(r.subtotal),
    ]);
    this.csv.download(this.csv.fileName('vendas_itens'), header, data);
  }

  // ---------- Helpers de template ----------
  kitTags(s: AdminSaleListItem): string[] {
    const tags: string[] = [];
    if (s.items.some(i => i.includesBook)) tags.push('Físico');
    if (s.items.some(i => i.includesEbook)) tags.push('E-book');
    if (s.items.some(i => i.includesVideo)) tags.push('Vídeo');
    return tags;
  }

  /** Marcos da linha de etapas: [label, data, aplica]. */
  steps(s: AdminSaleListItem): { label: string; date?: string | null; applies: boolean }[] {
    const nfe = s.datetimeNfeGenerated ?? (s.linkXml && s.linkDanfe ? (s.datetimeXmlInclusion ?? s.datetimeDanfeInclusion) : null);
    return [
      { label: 'NF-e gerada', date: s.linkXml && s.linkDanfe ? nfe : null, applies: true },
      { label: 'Etiqueta gerada', date: s.linkCorreiosCartaRemessa ? (s.datetimeLabelGenerated ?? s.datetimeXmlInclusion) : null, applies: s.hasPhisicalBook },
      { label: 'Início da produção', date: s.datetimeProductionStarted, applies: s.hasPhisicalBook },
      { label: 'Saída da gráfica', date: s.sendDateTime, applies: s.hasPhisicalBook },
      { label: 'Entregue', date: s.datetimeDelivered, applies: s.hasPhisicalBook },
    ];
  }

  isCurrentStep(s: AdminSaleListItem, index: number): boolean {
    const st = this.steps(s);
    for (let i = 0; i < st.length; i++) {
      if (!st[i].applies) continue;
      if (!st[i].date) return i === index;
    }
    return false;
  }

  lastStep(s: AdminSaleListItem): { label: string; date?: string | null } | null {
    const done = this.steps(s).filter(x => x.applies && x.date);
    return done.length ? done[done.length - 1] : { label: 'Pago', date: null };
  }

  trackBySale = (_: number, s: AdminSaleListItem) => s.idKitPayment;
  trackByStage = (_: number, s: AdminSalesStage) => s.status;
}
